import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Pill from '@mulesoft/anypoint-components/lib/Pill';
import { getScenarioConfigFromProviders } from './agentProvidersService';
import styles from './DynamicAuthenticationFields.css';

/**
 * ChipTextField – a self-contained chip/pill input.
 * Accepts an array of strings and exposes onAdd / onRemove callbacks.
 * Interaction matches the Recipient's Email chip input pattern.
 */
const ChipTextField = ({
  id,
  items,
  onAdd,
  onAddBatch,
  onRemove,
  placeholder,
  validationPattern,
  errorMessage,
  maxItems
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const errorTimerRef = useRef(null);

  const focusInput = useCallback(() => {
    const element = document.getElementById(id);

    if (element) {
      element.focus();
    }
  }, [id]);

  const setTimedError = useCallback((msg) => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
    }

    setInputError(msg);
    errorTimerRef.current = setTimeout(() => {
      setInputError('');
      errorTimerRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    },
    []
  );

  const addSingleItem = useCallback(
    (raw) => {
      const trimmed = raw.trim();

      if (!trimmed) return false;

      if (validationPattern && !validationPattern.test(trimmed)) {
        setTimedError(errorMessage || 'Invalid value');

        return false;
      }

      if (items.includes(trimmed)) {
        setTimedError('Already added');

        return false;
      }

      if (maxItems != null && items.length >= maxItems) {
        setTimedError('Maximum items reached');

        return false;
      }

      setInputError('');
      onAdd(trimmed);

      return true;
    },
    [items, onAdd, validationPattern, errorMessage, maxItems, setTimedError]
  );

  const handleChange = useCallback(
    ({ value }) => {
      if (inputError) {
        setInputError('');

        if (errorTimerRef.current) {
          clearTimeout(errorTimerRef.current);
          errorTimerRef.current = null;
        }
      }

      setInputValue(value || '');
    },
    [inputError]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const tokens = pastedText
        .split(/[,;\n]+/)
        .map((v) => v.trim())
        .filter((v) => v);

      if (!tokens.length) return;

      const existingSet = new Set(items);
      const accepted = [];
      const rejected = [];
      const remainingSlots =
        maxItems != null ? maxItems - items.length : Infinity;

      tokens.forEach((token) => {
        if (validationPattern && !validationPattern.test(token)) {
          rejected.push(token);
        } else if (!existingSet.has(token)) {
          if (accepted.length >= remainingSlots) {
            rejected.push(token);
          } else {
            existingSet.add(token);
            accepted.push(token);
          }
        }
      });

      if (accepted.length > 0) {
        if (onAddBatch) {
          onAddBatch(accepted);
        } else {
          accepted.forEach((v) => onAdd(v));
        }
      }

      if (rejected.length > 0) {
        setInputValue(rejected.join(', '));
        setTimedError(
          `${rejected.length} value${rejected.length > 1 ? 's' : ''} could not be added`
        );
      } else {
        setInputValue('');
      }
    },
    [items, onAdd, onAddBatch, validationPattern, maxItems, setTimedError]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (
        e.key === 'Enter' ||
        e.key === 'Tab' ||
        e.key === ' ' ||
        e.key === ','
      ) {
        e.preventDefault();

        if (inputValue.trim()) {
          addSingleItem(inputValue);
          setInputValue('');
        }
      }

      if (e.key === 'Backspace' && !inputValue) {
        if (items.length > 0) {
          onRemove(items[items.length - 1]);
        }
      }
    },
    [inputValue, addSingleItem, items, onRemove]
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addSingleItem(inputValue);
      setInputValue('');
    }
  }, [inputValue, addSingleItem]);

  return (
    <div>
      <div
        className={`${styles['chip-container']} ${
          inputError ? styles['chip-container--error'] : ''
        }`}
        data-chip-scroll
        onClick={focusInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            focusInput();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={styles['chip-content']}>
          {items.map((item) => (
            <Pill
              key={item}
              testId={`chip-${id}-${item}`}
              className={styles['chip-pill']}
              onRemove={() => onRemove(item)}
            >
              {item}
            </Pill>
          ))}
          <TextField
            id={id}
            testId={`${id}-input`}
            type="text"
            className={styles['chip-input']}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onPaste={handlePaste}
            placeholder={items.length === 0 ? placeholder : ''}
          />
        </div>
      </div>
      {inputError && (
        <div className={styles['chip-error-message']} role="alert">
          {inputError}
        </div>
      )}
    </div>
  );
};

ChipTextField.propTypes = {
  id: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddBatch: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  validationPattern: PropTypes.instanceOf(RegExp),
  errorMessage: PropTypes.string,
  maxItems: PropTypes.number
};

ChipTextField.defaultProps = {
  placeholder: '',
  onAddBatch: null,
  validationPattern: null,
  errorMessage: 'Invalid value',
  maxItems: undefined
};

/**
 * Renders connection parameter fields dynamically from the Outform Fields SDUI schema.
 * field.id maps 1:1 to backend API payload keys.
 * Required state and asterisk come only from the schema (requiredIn.create / requiredIn.edit).
 * Uses unique classes auth-fields--dynamic and field--dynamic for styling.
 *
 * Supported field types: text, password, static-select, chip-input.
 */
const DynamicAuthenticationFields = ({
  mode,
  targetSystem,
  authMethod,
  formData,
  onChange,
  context,
  providersLoaded
}) => {
  const scenarioConfig = useMemo(
    () => getScenarioConfigFromProviders(targetSystem, authMethod, context),
    [targetSystem, authMethod, context, providersLoaded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleFieldChange = useCallback(
    (formKey, value) => {
      onChange(formKey, value);
    },
    [onChange]
  );

  // Copy feedback state for info-field pills
  const [copiedText, setCopiedText] = useState(null);
  const copiedTimer = useRef(null);

  // Chip-input state management (keyed by field.id)
  const [chipInputValues, setChipInputValues] = useState({});
  const [chipInputErrors, setChipInputErrors] = useState({});
  const chipErrorTimers = useRef({});
  const chipInputRefs = useRef({});

  const setTimedChipError = useCallback((fieldId, msg) => {
    if (chipErrorTimers.current[fieldId]) {
      clearTimeout(chipErrorTimers.current[fieldId]);
    }

    setChipInputErrors((prev) => ({ ...prev, [fieldId]: msg }));
    chipErrorTimers.current[fieldId] = setTimeout(() => {
      setChipInputErrors((prev) => ({ ...prev, [fieldId]: '' }));
      chipErrorTimers.current[fieldId] = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      Object.values(chipErrorTimers.current).forEach((t) => {
        if (t) clearTimeout(t);
      });
    },
    []
  );

  if (!scenarioConfig || !scenarioConfig.fields?.length) {
    return null;
  }

  const contextKey = mode === 'edit' ? 'edit' : 'create';

  return (
    <div
      className={`${styles['auth-fields']} ${styles['auth-fields--dynamic']}`}
      data-test-id="dynamic-auth-fields"
    >
      {scenarioConfig.fields.map((field) => {
        const value =
          field.type === 'chip-input'
            ? (formData[field.id] ?? [])
            : (formData[field.id] ?? '');
        const placeholder =
          field.placeholderIn?.[contextKey] ??
          field.placeholderIn?.create ??
          '';
        const required =
          field.requiredIn?.[contextKey] ?? field.requiredIn?.create ?? false;
        const inputId = `auth-field-${field.id}`;

        if (field.type === 'chip-input') {
          const pattern = field.validation?.pattern
            ? new RegExp(field.validation.pattern)
            : null;
          const chipValue = chipInputValues[field.id] || '';
          const chipError = chipInputErrors[field.id] || '';
          const chipErrorMessage =
            field.validation?.errorMessage || 'Invalid value';

          const addSingleItem = (raw) => {
            const trimmed = raw.trim();

            if (!trimmed) return false;

            if (pattern && !pattern.test(trimmed)) {
              setTimedChipError(field.id, chipErrorMessage);

              return false;
            }

            if (value.includes(trimmed)) {
              setTimedChipError(field.id, 'Already added');

              return false;
            }

            if (field.maxItems != null && value.length >= field.maxItems) {
              setTimedChipError(field.id, 'Maximum items reached');

              return false;
            }

            setChipInputErrors((prev) => ({ ...prev, [field.id]: '' }));

            if (chipErrorTimers.current[field.id]) {
              clearTimeout(chipErrorTimers.current[field.id]);
              chipErrorTimers.current[field.id] = null;
            }

            handleFieldChange(field.id, [...value, trimmed]);

            return true;
          };

          const focusChipInput = () => {
            if (chipInputRefs.current[field.id]) {
              chipInputRefs.current[field.id].focus();
            }
          };

          return (
            <div
              key={field.id}
              className={`${styles.field} ${styles['field--dynamic']}`}
            >
              <label htmlFor={inputId} className={styles.label}>
                {field.label}
                {required && <span className={styles.required}> *</span>}
              </label>
              <div className={styles['field-content']}>
                <div>
                  <div
                    className={`${styles['chip-container']} ${
                      chipError ? styles['chip-container--error'] : ''
                    }`}
                    data-chip-scroll
                    onClick={focusChipInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        focusChipInput();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles['chip-content']}>
                      {value.map((item) => (
                        <Pill
                          key={item}
                          testId={`chip-${inputId}-${item}`}
                          className={styles['chip-pill']}
                          onRemove={() =>
                            handleFieldChange(
                              field.id,
                              value.filter((v) => v !== item)
                            )
                          }
                        >
                          {item}
                        </Pill>
                      ))}
                      <TextField
                        id={inputId}
                        testId={`${inputId}-input`}
                        type="text"
                        className={styles['chip-input']}
                        value={chipValue}
                        onChange={({ value: v }) => {
                          if (chipError) {
                            setChipInputErrors((prev) => ({
                              ...prev,
                              [field.id]: ''
                            }));

                            if (chipErrorTimers.current[field.id]) {
                              clearTimeout(chipErrorTimers.current[field.id]);
                              chipErrorTimers.current[field.id] = null;
                            }
                          }

                          setChipInputValues((prev) => ({
                            ...prev,
                            [field.id]: v || ''
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' ||
                            e.key === 'Tab' ||
                            e.key === ' ' ||
                            e.key === ','
                          ) {
                            e.preventDefault();

                            if (chipValue.trim()) {
                              addSingleItem(chipValue);
                              setChipInputValues((prev) => ({
                                ...prev,
                                [field.id]: ''
                              }));
                            }
                          }

                          if (e.key === 'Backspace' && !chipValue) {
                            if (value.length > 0) {
                              handleFieldChange(
                                field.id,
                                value.filter((_, i) => i !== value.length - 1)
                              );
                            }
                          }
                        }}
                        onBlur={() => {
                          if (chipValue.trim()) {
                            addSingleItem(chipValue);
                            setChipInputValues((prev) => ({
                              ...prev,
                              [field.id]: ''
                            }));
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          const tokens = pastedText
                            .split(/[,;\n]+/)
                            .map((v) => v.trim())
                            .filter((v) => v);

                          if (!tokens.length) return;

                          const existingSet = new Set(value);
                          const accepted = [];
                          const rejected = [];
                          const remainingSlots =
                            field.maxItems != null
                              ? field.maxItems - value.length
                              : Infinity;

                          tokens.forEach((token) => {
                            if (pattern && !pattern.test(token)) {
                              rejected.push(token);

                              return;
                            }

                            if (existingSet.has(token)) {
                              return;
                            }

                            if (accepted.length >= remainingSlots) {
                              rejected.push(token);

                              return;
                            }

                            existingSet.add(token);
                            accepted.push(token);
                          });

                          if (accepted.length > 0) {
                            handleFieldChange(field.id, [
                              ...value,
                              ...accepted
                            ]);
                          }

                          if (rejected.length > 0) {
                            setChipInputValues((prev) => ({
                              ...prev,
                              [field.id]: rejected.join(', ')
                            }));
                            setTimedChipError(
                              field.id,
                              `${rejected.length} value${
                                rejected.length > 1 ? 's' : ''
                              } could not be added`
                            );
                          } else {
                            setChipInputValues((prev) => ({
                              ...prev,
                              [field.id]: ''
                            }));
                          }
                        }}
                        placeholder={value.length === 0 ? placeholder : ''}
                      />
                    </div>
                  </div>
                  {chipError && (
                    <div className={styles['chip-error-message']} role="alert">
                      {chipError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (field.type === 'static-select') {
          const options = field.options ?? [];

          return (
            <div
              key={field.id}
              className={`${styles.field} ${styles['field--dynamic']}`}
            >
              <label htmlFor={inputId} className={styles.label}>
                {field.label}
                {required && <span className={styles.required}> *</span>}
              </label>
              <div className={styles['field-content']}>
                <Select
                  id={inputId}
                  name={field.id}
                  testId={`${field.id}-select`}
                  value={value}
                  placeholder={placeholder}
                  options={options}
                  onChange={(event) =>
                    handleFieldChange(
                      field.id,
                      event.itemsSelected?.value ?? ''
                    )
                  }
                  required={required}
                />
              </div>
            </div>
          );
        }

        if (field.type === 'info') {
          const permissionPattern =
            /(`[^`]+`|[\w-]+:[\w-]+|[A-Z][A-Z0-9]*_[A-Z0-9_]+)/g;
          const content = field.content || '';
          const tokens = [];
          let lastIndex = 0;
          let match = permissionPattern.exec(content);

          while (match) {
            if (match.index > lastIndex) {
              tokens.push({
                type: 'text',
                value: content.slice(lastIndex, match.index)
              });
            }

            const raw = match[0].startsWith('`')
              ? match[0].slice(1, -1)
              : match[0];

            tokens.push({ type: 'code', value: raw });
            lastIndex = match.index + match[0].length;
            match = permissionPattern.exec(content);
          }

          if (lastIndex < content.length) {
            tokens.push({ type: 'text', value: content.slice(lastIndex) });
          }

          const handleCopy = (text) => {
            const onSuccess = () => {
              if (copiedTimer.current) clearTimeout(copiedTimer.current);
              setCopiedText(text);
              copiedTimer.current = setTimeout(() => setCopiedText(null), 2000);
            };

            const fallbackCopy = () => {
              const textarea = document.createElement('textarea');

              textarea.value = text;
              textarea.style.position = 'fixed';
              textarea.style.opacity = '0';
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand('copy');
              document.body.removeChild(textarea);
              onSuccess();
            };

            if (navigator.clipboard) {
              navigator.clipboard
                .writeText(text)
                .then(onSuccess)
                .catch(fallbackCopy);
            } else {
              fallbackCopy();
            }
          };

          return (
            <div
              key={field.id}
              className={styles['info-field']}
              data-test-id={`info-field-${field.id}`}
            >
              <p className={styles['info-field-text']}>
                {tokens.map((token, i) =>
                  token.type === 'code' ? (
                    <span key={i} className={styles['info-field-pill']}>
                      <code className={styles['info-field-code']}>
                        {token.value}
                      </code>
                      <button
                        type="button"
                        className={`${styles['info-field-copy']}${copiedText === token.value ? ` ${styles['info-field-copy--copied']}` : ''}`}
                        onClick={() => handleCopy(token.value)}
                        aria-label={
                          copiedText === token.value
                            ? `Copied ${token.value}`
                            : `Copy ${token.value}`
                        }
                        title={
                          copiedText === token.value
                            ? 'Copied!'
                            : 'Copy to clipboard'
                        }
                      >
                        {copiedText === token.value ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0z" />
                          </svg>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M4 4V1.5C4 .67 4.67 0 5.5 0h9c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H12v2.5c0 .83-.67 1.5-1.5 1.5h-9C.67 16 0 15.33 0 14.5v-9C0 4.67.67 4 1.5 4H4zm1.5-2.5v2.5H10.5c.83 0 1.5.67 1.5 1.5V10.5h2.5v-9h-9zM1.5 5.5v9h9v-9h-9z" />
                          </svg>
                        )}
                      </button>
                    </span>
                  ) : (
                    <span key={i}>{token.value}</span>
                  )
                )}
              </p>
            </div>
          );
        }

        const isPassword = field.type === 'password';

        return (
          <div
            key={field.id}
            className={`${styles.field} ${styles['field--dynamic']}`}
          >
            <label htmlFor={inputId} className={styles.label}>
              {field.label}
              {required && <span className={styles.required}> *</span>}
            </label>
            <div className={styles['field-content']}>
              <TextField
                id={inputId}
                name={field.id}
                type={isPassword ? 'password' : 'text'}
                value={value}
                onChange={(e) => handleFieldChange(field.id, e.value)}
                placeholder={placeholder}
                required={required}
                testId={`${field.id}-input`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

DynamicAuthenticationFields.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  targetSystem: PropTypes.string,
  authMethod: PropTypes.string,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
  providersLoaded: PropTypes.bool
};

DynamicAuthenticationFields.defaultProps = {
  mode: 'add',
  targetSystem: '',
  authMethod: '',
  context: undefined,
  providersLoaded: false
};

export { ChipTextField };
export default DynamicAuthenticationFields;
